/**
 * @component components/masterclass/card
 * @description Register countdowns, ajax event, etc. for masterclass cards.
 */

import {CountdownClock} from '../../modules/countdownClock.js';

/**
 * Observers countdown timer and disables masterclass card when the masterclass is being started.
 * @param {HTMLElement} card
 */
function initCountdown(card) {
    /** @type {HTMLElement} */
    const countdownElement = card.querySelector('.js-countdownClock');
    
    if (countdownElement) {
        // Skip JavaScript countdown initialization if static display is requested
        // Check for data-static-display on the first child (the actual countdown component)
        const countdownContent = countdownElement.firstElementChild;
        if (countdownContent && countdownContent.getAttribute('data-static-display') === 'true') {
            return;
        }

        // Get the timestamp from the masterclass start time
        // In masterclass component, it uses $masterclass->start_at for the countdown
        const startTimestamp = Number(card.dataset.startTimestamp);
        
        if (!isNaN(startTimestamp) && startTimestamp > 0) {
            new CountdownClock(countdownElement, startTimestamp, () => {
                const viewMasterclassButton = card.querySelector('.js-countdownDependentCardState');
                if (viewMasterclassButton) {
                    viewMasterclassButton.textContent = card.dataset.onAirText;
                }
                card.classList.add('card--on-air');
            });
        }
    }
}

/**
 * @param {HTMLElement} card
 */
function initProgressOnViewport(card) {
    /** @type {?HTMLElement} */
    const circularProgress = card.querySelector('.js-progressCircular');
    if (!circularProgress) return;

    const percentage = Number(circularProgress.dataset.percentage);

    const observer = new IntersectionObserver(
        (entries, observer) => {
            entries.forEach((circularProgressObserverEntry) => {
                if (circularProgressObserverEntry.intersectionRatio >= 1) {
                    const target = /** @type {HTMLElement} */ (circularProgressObserverEntry.target);
                    updateProgress(target, percentage);
                    observer.unobserve(circularProgressObserverEntry.target);
                }
            });
        },
        {
            threshold: 1.0,
        }
    );
    observer.observe(circularProgress);
}

/**
 * @param {HTMLElement} progressRing
 * @param {number} percentage
 */
function updateProgress(progressRing, percentage) {
    const filledOutIndicator = progressRing.querySelector('.js-filledOutIndicator');
    const radius = Number(filledOutIndicator.getAttribute('r'));
    const circumference = Math.PI * (radius * 2);
    const strokeDashoffset = ((100 - percentage) / 100) * circumference;

    if (percentage === 100) {
        progressRing.classList.add('progressCircular--complete');
    }

    filledOutIndicator.setAttribute('style', `stroke-dashoffset: ${strokeDashoffset}px`);
}

/**
 * Initialize card countdown and stroke fill %
 * @param {HTMLElement} card
 */
export function initCardAnimations(card) {
    initCountdown(card);
    initProgressOnViewport(card);
}

/**
 * Initialize component, register all event handlers
 */
function initializeComponent() {
    const masterclassCards = [...document.querySelectorAll('[data-masterclass-id]')];
    masterclassCards.forEach(
        /** @param {HTMLElement} card */
        (card) => {
            initCardAnimations(card);
        }
    );
}

// init component when document is ready
document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', initializeComponent)
    : initializeComponent();
